import { useQuery } from '@tanstack/react-query';
import { getManualPostIngestPipelineSlides, PostIngestPipelineSelectedSlide } from 'api/platform';
import { MULTIPLEX_STAIN_ID } from 'interfaces/stainType';
import { filter, first, intersection, map, size } from 'lodash';
import { useMemo } from 'react';
import { CasesParams } from 'utils/useCasesParams/types';

export type PostIngestStainFamily = 'brightfield' | 'mIF';

export interface PostIngestFamilySelection {
  stainFamily: PostIngestStainFamily;
  slides: PostIngestPipelineSelectedSlide[];
  casesParams: CasesParams;
  canRunPostIngestPipeline: boolean;
  postIngestPipelineReason?: string;
  availableDnaChannels?: string[];
}

export const resolvePostIngestPipelineSelection = ({
  casesParams,
  slides,
}: {
  casesParams: CasesParams;
  slides?: PostIngestPipelineSelectedSlide[];
}): PostIngestFamilySelection[] => {
  if (!slides) {
    return [];
  }

  const buildFamilySelection = (
    stainFamily: PostIngestStainFamily,
    familySlides: PostIngestPipelineSelectedSlide[]
  ): PostIngestFamilySelection | null => {
    if (!size(familySlides)) {
      return null;
    }

    const invalidSlides = filter(familySlides, ({ canRunPostIngestPipeline }) => !canRunPostIngestPipeline);
    const familyReason =
      stainFamily === 'mIF'
        ? getMifFamilySelectionReason(familySlides)
        : first(map(invalidSlides, 'postIngestPipelineReason')) || undefined;

    const availableDnaChannels =
      stainFamily === 'mIF'
        ? map(familySlides, ({ availableDnaChannels: slideDnaChannels }) => slideDnaChannels || []).reduce<string[]>(
            (commonChannels, slideChannels) => intersection(commonChannels, slideChannels),
            familySlides[0]?.availableDnaChannels || []
          )
        : undefined;

    return {
      stainFamily,
      slides: familySlides,
      casesParams: {
        ...casesParams,
        slidesMode: true,
        caseIdsToInclude: undefined,
        caseIdsToExclude: undefined,
        slideIdsToInclude: map(familySlides, 'slideId'),
        slideIdsToExclude: undefined,
      },
      canRunPostIngestPipeline: !familyReason,
      postIngestPipelineReason: familyReason,
      ...(availableDnaChannels ? { availableDnaChannels } : {}),
    };
  };

  return filter(
    [
      buildFamilySelection(
        'brightfield',
        filter(slides, ({ stainingType }) => stainingType !== MULTIPLEX_STAIN_ID)
      ),
      buildFamilySelection(
        'mIF',
        filter(slides, ({ stainingType }) => stainingType === MULTIPLEX_STAIN_ID)
      ),
    ],
    Boolean
  ) as PostIngestFamilySelection[];
};

const getMifFamilySelectionReason = (slides: PostIngestPipelineSelectedSlide[]): string | undefined => {
  const invalidSlides = filter(slides, ({ canRunPostIngestPipeline }) => !canRunPostIngestPipeline);
  if (size(invalidSlides)) {
    if (size(invalidSlides) === 1) {
      return invalidSlides[0]?.postIngestPipelineReason || undefined;
    }

    return `${size(
      invalidSlides
    )} selected mIF slides are missing a supported DNA channel. Repair slide channel annotations or remove those slides from the selection.`;
  }

  if (size(slides) <= 1) {
    return undefined;
  }

  const sharedDnaChannels = map(slides, ({ availableDnaChannels }) => availableDnaChannels || []).reduce<string[]>(
    (commonChannels, slideChannels) => intersection(commonChannels, slideChannels),
    slides[0]?.availableDnaChannels || []
  );
  if (size(sharedDnaChannels)) {
    return undefined;
  }

  return 'Selected mIF slides do not share a supported DNA channel. Select slides with a shared supported DNA channel or repair slide channel annotations.';
};

export const usePostIngestPipelineSelection = (casesParams: CasesParams) => {
  const studyId = casesParams.studyId ?? casesParams.filters?.studyId;
  const slidesQuery = useQuery({
    queryKey: ['postIngestPipelineSelectionSlides', casesParams],
    queryFn: ({ signal }) => getManualPostIngestPipelineSlides(casesParams, signal),
    enabled: Boolean(studyId),
  });
  const familySelections = useMemo(
    () => resolvePostIngestPipelineSelection({ casesParams, slides: slidesQuery.data }),
    [casesParams, slidesQuery.data]
  );

  return {
    familySelections,
    isLoadingSelection:
      Boolean(studyId) && (slidesQuery.isLoading || (!slidesQuery.isError && slidesQuery.data === undefined)),
    selectionError: slidesQuery.isError ? 'Could not load the selected slides. Try again.' : undefined,
    hasSelectedSlides: size(slidesQuery.data) > 0,
  };
};
